import React, { memo, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import getInputStyles, {
  ButtonsStyled,
  ButtonStyled,
  PrimaryButtonStyled,
  RadioContainer,
  HiddenInput,
} from './styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import Radio from '@material-ui/core/Radio';
import theme from '../../themes/default';

const ActionProperties = PropTypes.shape({
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
});

const ButtonActions = PropTypes.shape({
  primary: ActionProperties,
  secondary: ActionProperties,
});

const PurpleRadio = withStyles({
  root: {
    '&$checked': {
      color: theme.color.purple3,
    },
  },
  checked: {},
})((props) => <Radio color="default" {...props} />);

const DEFAULT = '';

const getFilteredArray = (array) => {
  return array.filter((item) => item !== undefined);
};

const getValue = (selectedValue, data) => {
  if (Array.isArray(selectedValue)) {
    const values = data
      .filter((item) => selectedValue.includes(item.key))
      .map((item) => item.value);

    if (values.length > 1) {
      return values.join(', ');
    }
    return values;
  }

  if (selectedValue !== DEFAULT && data.length > 0) {
    return data.filter((item) => selectedValue === item.key)[0].value;
  }
  return '';
};

const genId = () => Math.random().toString(36).substr(2, 9);

const SelectInput = memo(
  ({
    classes,
    data,
    handleChange,
    value,
    isOpen,
    hasFooter,
    hasMultiSelect,
    label,
    disabled,
    autoFocus,
    hasError,
    errorMessage,
    helperText,
    name,
    hasPlaceholder,
    buttonActions,
    isCustom,
    tabbable,
    'data-testid': dataTestId,
  }) => {
    const hasInitialValue = Array.isArray(value)
      ? !!value.length
      : value !== DEFAULT;
    const inputLabelRef = useRef();
    const inputLabelDivRef = useRef();

    const [labelWidth, setLabelWidth] = useState(10);
    const [open, setOpen] = useState(false);
    const [labelInOffset, setLabelInOffset] = useState(hasInitialValue);

    const [id] = useState(genId());
    const selectId = `select-${id}`;
    const inputLabelId = `input-label-${id}`;

    const handleOpen = () => {
      setOpen(true);
      setLabelInOffset(true);
    };

    const handleClose = () => {
      setOpen(false);
      if (
        value === DEFAULT ||
        (Array.isArray(value) && getFilteredArray(value).length === 0)
      ) {
        setLabelInOffset(false);
      }
    };

    const handleSetValue = (e) => {
      if (Array.isArray(e.target.value)) {
        if (open) {
          setLabelInOffset(true);
        }
      } else {
        setLabelInOffset(e.target.value !== DEFAULT);
      }
      handleChange(e);
    };

    const handleItemKeydown = (e) => {
      if (e.key === ' ') {
        e.target.click();
      }
    };

    const getRoot = (open, hasError, value, hasMultiSelect) => {
      if (hasMultiSelect) {
        return open ? classes.rootOpen : classes.root;
      }
      if (open && hasError) {
        return classes.openWithError;
      }
      if (hasError) {
        if (value !== DEFAULT) {
          return classes.errorWithValue;
        }
        return classes.error;
      }
      if (!open && !hasError && value !== DEFAULT) {
        return classes.rootWithValue;
      }
      return open ? classes.rootOpen : classes.root;
    };

    const getRootForLabel = (hasError) => {
      if (!labelInOffset && hasError) {
        return classes.selectInputLabelInLineWithError;
      }
      if (!labelInOffset) {
        return classes.selectInputLabelInLine;
      }
      if (hasError) {
        return classes.selectInputLabelWithError;
      }
      return classes.selectInputLabel;
    };

    const getRuleForSelect = (hasError) => {
      if (hasError) {
        return classes.selectInputLabelFocusedWithError;
      } else {
        if (labelInOffset) {
          return classes.selectInputLabelFocused;
        } else {
          return classes.selectInputLabelFocusedInline;
        }
      }
    };

    const getRuleForMenuItem = (isCustom, isDisabled) => {
      if (isDisabled) {
        return classes.selectInputDisabled;
      }
      if (isCustom) {
        return classes.selectInputCustom;
      } else {
        return classes.selectInput;
      }
    };

    const clickPrimaryButton = (e) => {
      e.stopPropagation();
      buttonActions.primary.onClick();
      setOpen(false);
      setLabelInOffset(
        (!Array.isArray(value) && value !== DEFAULT) ||
          (Array.isArray(value) && getFilteredArray(value).length !== 0),
      );
    };

    const clickSecondaryButton = (e) => {
      e.stopPropagation();
      buttonActions.secondary.onClick();
    };

    useEffect(() => {
      const actualWidth = ReactDOM.findDOMNode(
        inputLabelDivRef.current,
      ).getBoundingClientRect().width;
      setLabelWidth(actualWidth * 0.92);
    }, [inputLabelDivRef]);

    return (
      <FormControl
        variant="outlined"
        size="small"
        className={classes.formControl}
        disabled={disabled}
        data-testid={dataTestId}
      >
        {
          <InputLabel
            ref={inputLabelRef}
            id={inputLabelId}
            classes={{
              root: getRootForLabel(hasError),
              focused: getRuleForSelect(hasError),
              disabled: classes.selectInputLabelDisabled,
            }}
          >
            {label}
          </InputLabel>
        }
        <Select
          value={value}
          renderValue={() => getValue(value, data)}
          multiple={hasMultiSelect}
          onChange={handleSetValue}
          className={classes.select}
          open={open || isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          SelectDisplayProps={{
            id: selectId,
            'aria-haspopup': 'listbox',
            'aria-pressed': undefined,
            'aria-labelledby': `${inputLabelId} ${selectId} `,
            tabIndex: tabbable ? 0 : -1,
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
            MenuListProps: {
              className: classes.selectList,
            },
          }}
          input={
            <OutlinedInput
              name={name}
              classes={{
                input: classes.input,
                root: getRoot(open, hasError, value, hasMultiSelect),
                disabled: classes.rootDisabled,
                notchedOutline: classes.notchedOutline,
                focused: classes.focused,
              }}
              autoFocus={autoFocus}
              labelWidth={labelInOffset ? labelWidth : 0}
              id="outlined-select-input"
            />
          }
        >
          {hasPlaceholder && (
            <MenuItem
              value={DEFAULT}
              classes={{
                root: hasError
                  ? classes.selectInputSelected
                  : classes.selectInputSelectedWithError,
              }}
            >
              Select a value
            </MenuItem>
          )}
          {data.map(
            (item) =>
              !!item && (
                <MenuItem
                  key={item.key}
                  value={item.key}
                  onKeyDown={handleItemKeydown}
                  classes={{
                    root: getRuleForMenuItem(isCustom, item.isDisabled),
                  }}
                >
                  {isCustom ? (
                    <RadioContainer onClick={item.onClick}>
                      <PurpleRadio checked={item.isChecked} />
                      <span>{item.value}</span>
                    </RadioContainer>
                  ) : (
                    item.value
                  )}
                </MenuItem>
              ),
          )}
          {hasFooter && (
            <div
              // eslint-disable-next-line react/no-unknown-property
              classes={{
                root: classes.selectInputCustomFooter,
              }}
            >
              <ButtonsStyled>
                <ButtonStyled
                  type="secondary"
                  onClick={clickSecondaryButton}
                  disabled={buttonActions.secondary.disabled}
                >
                  {buttonActions.secondary.label}
                </ButtonStyled>
                <PrimaryButtonStyled
                  type="primary"
                  onClick={clickPrimaryButton}
                  disabled={buttonActions.primary.disabled}
                >
                  {buttonActions.primary.label}
                </PrimaryButtonStyled>
              </ButtonsStyled>
            </div>
          )}
        </Select>

        {hasError && (
          <FormHelperText classes={{ root: classes.errorText }}>
            {errorMessage}
          </FormHelperText>
        )}
        {!hasError && helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
        <HiddenInput ref={inputLabelDivRef} htmlFor="outlined-select-input-div">
          {label}
        </HiddenInput>
      </FormControl>
    );
  },
);

SelectInput.displayName = 'SelectInput';

SelectInput.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootOpen: PropTypes.string.isRequired,
    rootDisabled: PropTypes.string.isRequired,
    rootWithValue: PropTypes.string.isRequired,
    openWithError: PropTypes.string.isRequired,
    errorWithValue: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    input: PropTypes.string.isRequired,
    formControl: PropTypes.string.isRequired,
    select: PropTypes.string.isRequired,
    selectInputSelected: PropTypes.string.isRequired,
    selectInput: PropTypes.string.isRequired,
    selectInputCustom: PropTypes.string.isRequired,
    selectInputDisabled: PropTypes.string.isRequired,
    selectInputLabel: PropTypes.string.isRequired,
    selectInputLabelInLine: PropTypes.string,
    selectInputLabelDisabled: PropTypes.string,
    selectInputLabelWithError: PropTypes.string,
    errorText: PropTypes.string,
    selectInputLabelFocused: PropTypes.string.isRequired,
    selectInputLabelFocusedInline: PropTypes.string.isRequired,
    selectInputLabelFocusedWithError: PropTypes.string.isRequired,
    selectList: PropTypes.string.isRequired,
    selectInputLabelInLineWithError: PropTypes.string.isRequired,
    selectInputSelectedWithError: PropTypes.string.isRequired,
    selectInputCustomFooter: PropTypes.string.isRequired,
    notchedOutline: PropTypes.string.isRequired,
    focused: PropTypes.string.isRequired,
  }),
  name: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isCustom: PropTypes.bool,
  hasFooter: PropTypes.bool,
  hasMultiSelect: PropTypes.bool,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  hasPlaceholder: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array,
  ]),
  buttonActions: ButtonActions,
  tabbable: PropTypes.bool,
  'data-testid': PropTypes.string,
};

SelectInput.defaultProps = {
  value: '',
  isCustom: false,
  hasPlaceholder: false,
  hasMultiSelect: false,
  hasError: false,
  hasFooter: false,
  helperText: '',
  tabbable: true,
  'data-testid': '',
};

export default withStyles(getInputStyles)(SelectInput);
